/**
 * Local channel/category structure mutations for the active Space: optimistic
 * inserts/removals plus a full layout apply with API confirmation and rollback.
 */

import {
  useCallback,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import type {
  createApiClient,
  PublicSpaceChannel,
  PublicSpaceChannelCategory,
  UpdateSpaceChannelLayoutParams,
} from '@adieuu/shared';

type ApiClient = ReturnType<typeof createApiClient>;

export interface SpaceChannelStructureParams {
  api: ApiClient;
  channels: PublicSpaceChannel[];
  categories: PublicSpaceChannelCategory[];
  setChannels: Dispatch<SetStateAction<PublicSpaceChannel[]>>;
  setCategories: Dispatch<SetStateAction<PublicSpaceChannelCategory[]>>;
  activeSpaceIdRef: MutableRefObject<string | null>;
}

export function useSpaceChannelStructure(params: SpaceChannelStructureParams) {
  const { api, channels, categories, setChannels, setCategories, activeSpaceIdRef } = params;

  const addChannelLocally = useCallback(
    (channel: PublicSpaceChannel) => {
      setChannels((prev) => {
        if (prev.some((c) => c.id === channel.id)) {
          return prev.map((c) => (c.id === channel.id ? channel : c));
        }
        return [...prev, channel].sort(
          (a, b) => a.position - b.position || a.id.localeCompare(b.id),
        );
      });
    },
    [setChannels],
  );

  const addCategoryLocally = useCallback(
    (category: PublicSpaceChannelCategory) => {
      setCategories((prev) => {
        if (prev.some((c) => c.id === category.id)) {
          return prev.map((c) => (c.id === category.id ? category : c));
        }
        return [...prev, category].sort(
          (a, b) => a.position - b.position || a.id.localeCompare(b.id),
        );
      });
    },
    [setCategories],
  );

  const removeCategoryLocally = useCallback(
    (categoryId: string) => {
      const removed = categories.find((c) => c.id === categoryId);
      const promoteTo = removed?.parentCategoryId ?? null;
      setCategories((prev) =>
        prev
          .filter((c) => c.id !== categoryId)
          .map((c) =>
            c.parentCategoryId === categoryId ? { ...c, parentCategoryId: promoteTo } : c,
          ),
      );
      setChannels((prev) =>
        prev.map((ch) =>
          ch.categoryId === categoryId ? { ...ch, categoryId: promoteTo } : ch,
        ),
      );
    },
    [categories, setCategories, setChannels],
  );

  const applyChannelLayout = useCallback(
    async (
      layout: UpdateSpaceChannelLayoutParams,
      options?: { knownCategories?: readonly PublicSpaceChannelCategory[] },
    ): Promise<boolean> => {
      const spaceId = activeSpaceIdRef.current;
      if (!spaceId) return false;

      const prevCategories = categories;
      const prevChannels = channels;

      const categoryById = new Map(categories.map((c) => [c.id, c]));
      for (const cat of options?.knownCategories ?? []) {
        categoryById.set(cat.id, cat);
      }
      const channelById = new Map(channels.map((c) => [c.id, c]));

      const nextCategories: PublicSpaceChannelCategory[] = [];
      const nextChannels: PublicSpaceChannel[] = [];

      for (const group of layout.groups) {
        group.items.forEach((item, position) => {
          if (item.type === 'channel') {
            const ch = channelById.get(item.id);
            if (ch) {
              nextChannels.push({
                ...ch,
                categoryId: group.parentCategoryId,
                position,
              });
            }
          } else {
            const cat = categoryById.get(item.id);
            if (cat) {
              nextCategories.push({
                ...cat,
                parentCategoryId: group.parentCategoryId,
                position,
              });
            }
          }
        });
      }

      setCategories(nextCategories);
      setChannels(nextChannels);

      try {
        const res = await api.spaces.updateChannelLayout(spaceId, layout);
        if (!res.success || !res.data) {
          setCategories(prevCategories);
          setChannels(prevChannels);
          return false;
        }
        setCategories(res.data.categories);
        setChannels(res.data.channels);
        return true;
      } catch {
        setCategories(prevCategories);
        setChannels(prevChannels);
        return false;
      }
    },
    [api, categories, channels, setCategories, setChannels, activeSpaceIdRef],
  );

  return {
    addChannelLocally,
    addCategoryLocally,
    removeCategoryLocally,
    applyChannelLayout,
  };
}
